import type { ChatMessage, ChatRoomView, Place, ReportCategory } from '@tomato/shared';
import { CHAT_LIMITS, formatPrice, REPORT_CATEGORIES, tradeStatusLabel } from '@tomato/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { Button, Checkbox, Notice, Select, Spinner, TextArea, TextField } from '../components/ui';
import { WalletPanel } from '../components/WalletPanel';
import { formatWhen } from '../lib/markdown';
import { useSessionStore } from '../store/session';

/** ISO(UTC) → datetime-local 입력값(YYYY-MM-DDTHH:mm, 로컬시각). */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function formatApptWhen(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 빈 대화방에서 첫 말을 돕는 제안. 누르면 입력창에 채워진다(그대로 보내거나 고쳐서 보냄). */
const STARTERS = [
  '혹시 아직 판매하시나요?',
  '구입하신 지 얼마나 되셨나요?',
  '직거래 가능할까요?',
  '가격 조금 조정 가능할까요?',
];

const pad2 = (n: number) => String(n).padStart(2, '0');
function captureStamp(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function msgTime(iso: string): string {
  const d = new Date(iso);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * 대화를 **일시가 찍힌 이미지 스크린샷**으로 렌더한다(신고 증거용).
 * html2canvas 대신 Canvas API로 직접 그린다 — Tailwind v4 oklch 색상 비호환·외부 의존성 회피.
 * 각 메시지의 시각과 캡처 일시가 함께 박혀 증거로서 맥락이 남는다.
 */
function renderConversationImage(messages: ChatMessage[], partner: string): string | null {
  const recent = messages.slice(-60);
  const W = 600;
  const PAD = 16;
  const LINE = 20;
  const GAP = 8;
  const HEADER = 48;
  const bodyFont = '14px -apple-system, "Apple SD Gothic Neo", sans-serif';
  const metaFont = '11px -apple-system, "Apple SD Gothic Neo", sans-serif';

  const measure = document.createElement('canvas').getContext('2d');
  if (!measure) return null;
  measure.font = bodyFont;
  const maxW = W - PAD * 2;
  const wrap = (text: string): string[] => {
    const out: string[] = [];
    let cur = '';
    for (const ch of text) {
      if (ch === '\n') {
        out.push(cur);
        cur = '';
        continue;
      }
      if (measure.measureText(cur + ch).width > maxW && cur) {
        out.push(cur);
        cur = ch;
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.length ? out : [''];
  };

  const blocks = recent.map((m) => ({
    who: m.kind === 'system' ? '시스템' : m.mine ? '나(신고자)' : partner,
    time: msgTime(m.createdAt),
    lines: wrap(m.body),
    system: m.kind === 'system',
  }));

  let h = HEADER + PAD;
  for (const b of blocks) h += LINE + b.lines.length * LINE + GAP;
  h += PAD;

  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = W * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(scale, scale);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, h);
  ctx.fillStyle = '#111111';
  ctx.font = 'bold 14px -apple-system, sans-serif';
  ctx.fillText('🍅 토마토마켓 대화 신고 캡처', PAD, 24);
  ctx.fillStyle = '#999999';
  ctx.font = metaFont;
  ctx.fillText(`캡처 일시 ${captureStamp()}`, PAD, 40);
  ctx.strokeStyle = '#eeeeee';
  ctx.beginPath();
  ctx.moveTo(0, HEADER);
  ctx.lineTo(W, HEADER);
  ctx.stroke();

  let y = HEADER + PAD + 4;
  for (const b of blocks) {
    ctx.fillStyle = b.system ? '#aaaaaa' : '#888888';
    ctx.font = metaFont;
    ctx.fillText(`[${b.time}] ${b.who}`, PAD, y);
    y += LINE;
    ctx.fillStyle = b.system ? '#999999' : '#111111';
    ctx.font = bodyFont;
    for (const ln of b.lines) {
      ctx.fillText(ln, PAD, y);
      y += LINE;
    }
    y += GAP;
  }
  return canvas.toDataURL('image/png');
}

function Bubble({ m }: { m: ChatMessage }) {
  if (m.kind === 'system') {
    return (
      <div className="my-2 text-center">
        <span className="inline-block rounded-full bg-ink-100 px-3 py-1 text-[12px] text-ink-500">
          {m.body}
        </span>
      </div>
    );
  }
  return (
    <div className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed whitespace-pre-wrap ${
          m.mine ? 'bg-tomato-600 text-white' : 'bg-surface text-ink-900 shadow-card'
        }`}
      >
        {m.body}
        <div className={`mt-1 text-[10px] ${m.mine ? 'text-white/70' : 'text-ink-400'}`}>
          {formatWhen(m.createdAt)}
        </div>
      </div>
    </div>
  );
}

export function ChatRoomScreen() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  // D7: 약속 확정 시 미연동자에게 리마인더 연동을 유도한다.
  const telegramLinked = useSessionStore((s) => s.me?.telegramLinked ?? false);
  const [room, setRoom] = useState<ChatRoomView | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState<ReportCategory>('abuse');
  const [reportReason, setReportReason] = useState('');
  const [reportAttach, setReportAttach] = useState(true);
  const [reportBlock, setReportBlock] = useState(false);
  const [reported, setReported] = useState(false);
  // 상대 지갑주소 패널(복사·QR) 토글.
  const [walletOpen, setWalletOpen] = useState(false);

  // 약속(Phase C)
  const [places, setPlaces] = useState<Place[]>([]);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeAt, setProposeAt] = useState('');
  const [proposePlace, setProposePlace] = useState('');
  const [arriveHint, setArriveHint] = useState('');
  const [arriveOpen, setArriveOpen] = useState(false);

  const cursorRef = useRef<string | null>(null);
  const pollingRef = useRef(false);
  const closedRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const markRead = useCallback(() => {
    void api.post(`/api/chats/${roomId}/read`).catch(() => {});
  }, [roomId]);

  // 약속 상태는 시스템 메시지로 흐르므로, 방 메타(appointment)를 다시 읽어 배너를 갱신한다.
  const refreshRoom = useCallback(async () => {
    try {
      const res = await api.get<{ room: ChatRoomView }>(`/api/chats/${roomId}`);
      setRoom(res.room);
      closedRef.current = res.room.closed;
    } catch {
      /* 조용히 넘긴다 */
    }
  }, [roomId]);

  // 새 메시지만 당겨온다(커서 이후). 인터벌·발신 후 공용.
  const pollOnce = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const q = cursorRef.current ? `?after=${encodeURIComponent(cursorRef.current)}` : '';
      const res = await api.get<{ items: ChatMessage[]; cursor: string | null }>(
        `/api/chats/${roomId}/messages${q}`,
      );
      cursorRef.current = res.cursor;
      if (res.items.length > 0) {
        setMessages((prev) => [...prev, ...res.items]);
        markRead();
        // 시스템 메시지 = 약속 확정/변경/취소·도착·종료 → 방 메타를 갱신한다.
        if (res.items.some((m) => m.kind === 'system')) void refreshRoom();
      }
    } catch {
      /* 폴링 실패는 조용히 넘긴다(다음 틱에 재시도) */
    } finally {
      pollingRef.current = false;
    }
  }, [roomId, markRead, refreshRoom]);

  // 최초 로드: 방 메타 + 전체 메시지 + 읽음.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessages([]);
    cursorRef.current = null;
    void (async () => {
      try {
        const [{ room }, msgs] = await Promise.all([
          api.get<{ room: ChatRoomView }>(`/api/chats/${roomId}`),
          api.get<{ items: ChatMessage[]; cursor: string | null }>(`/api/chats/${roomId}/messages`),
        ]);
        if (cancelled) return;
        setRoom(room);
        closedRef.current = room.closed;
        setMessages(msgs.items);
        cursorRef.current = msgs.cursor;
        markRead();
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : '대화를 불러오지 못했어요.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId, markRead]);

  // 방이 열려 있는 동안만 폴링. 이탈하면 멈춘다(SPEC 2-B).
  useEffect(() => {
    if (!room || room.closed) return;
    const t = setInterval(() => void pollOnce(), CHAT_LIMITS.pollMs);
    return () => clearInterval(t);
  }, [room, pollOnce]);

  // 새 메시지가 오면 맨 아래로.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || busy || !room || room.closed) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/chats/${roomId}/messages`, { body: text });
      setInput('');
      await pollOnce(); // 내가 보낸 것도 폴링으로 단일 경로로 반영(중복 방지).
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '메시지를 보내지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  // 약속 장소 프리셋 로드(제안 폼용).
  useEffect(() => {
    void api
      .get<{ items: Place[] }>('/api/places')
      .then((res) => {
        setPlaces(res.items);
        if (res.items[0]) setProposePlace(String(res.items[0].id));
      })
      .catch(() => {});
  }, []);

  async function proposeAppointment() {
    if (busy || !proposeAt || !proposePlace) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/chats/${roomId}/appointments`, {
        scheduledAt: new Date(proposeAt).toISOString(),
        placeId: Number(proposePlace),
      });
      setProposeOpen(false);
      setProposeAt('');
      await refreshRoom();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '약속을 제안하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  async function apptAction(path: string, body?: unknown) {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await api.post(path, body);
      await refreshRoom();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  async function submitReport() {
    if (busy) return;
    setBusy(true);
    try {
      // 첨부를 택했으면 대화를 일시가 찍힌 이미지로 캡처해 함께 보낸다.
      const snapshotImage =
        reportAttach && room ? renderConversationImage(messages, room.partnerNickname) : null;
      const res = await api.post<{ ok: boolean; left: boolean }>(`/api/chats/${roomId}/report`, {
        category: reportCategory,
        reason: reportReason.trim(),
        attachConversation: reportAttach,
        snapshotImage,
        blockAndLeave: reportBlock,
      });
      setReportOpen(false);
      // 차단하고 나가기를 택했으면 대화 목록으로 나간다. 아니면 접수 안내만.
      if (res.left) {
        navigate('/chats');
      } else {
        setReported(true);
      }
    } catch {
      setError('신고하지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="py-24 text-center">
        <Spinner />
      </div>
    );
  }
  if (!room) {
    return (
      <div className="py-24 text-center">
        <p className="text-[15px] text-ink-500">{error || '대화를 찾을 수 없어요.'}</p>
        <Link to="/chats" className="mt-4 inline-block text-[13px] text-tomato-600">
          대화 목록으로
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col py-2">
      {/* 헤더: 글 요약 + 상대 활동명 + 신고 */}
      <div className="flex items-center gap-3 py-3 hairline-b">
        <Link to="/chats" className="text-ink-400 transition hover:text-ink-700">
          ←
        </Link>
        <Link to={`/posts/${room.post.id}`} className="flex min-w-0 flex-1 items-center gap-2.5">
          {room.post.thumbnailUrl ? (
            <img
              src={room.post.thumbnailUrl}
              alt=""
              className="size-9 shrink-0 rounded-lg bg-ink-100 object-cover"
            />
          ) : (
            <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-300">
              🍅
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium text-ink-900">{room.partnerNickname}</p>
            <p className="truncate text-[12px] text-ink-400">
              {room.post.title}
              {formatPrice(room.post.price, room.post.tradeType) &&
                ` · ${formatPrice(room.post.price, room.post.tradeType)}`}
              {room.post.tradeStatus && room.post.tradeStatus !== 'selling'
                ? ` · ${tradeStatusLabel(room.post.tradeStatus, room.post.tradeType)}`
                : ''}
            </p>
          </div>
        </Link>
        {/* 상대가 지갑주소를 등록했을 때만 — 송금할 주소를 여기서 바로 복사한다. */}
        {room.partnerWalletAddress && (
          <button
            onClick={() => setWalletOpen((v) => !v)}
            className="shrink-0 text-[12px] text-ink-400 transition hover:text-ink-700"
          >
            💳 지갑
          </button>
        )}
        {/* 약속 잡기는 부가 기능 — 헤더로 옮겼다. 활성 약속이 없을 때만 노출. */}
        {!room.closed && !(room.appointment && room.appointment.status !== 'cancelled') && (
          <button
            onClick={() => setProposeOpen((v) => !v)}
            className="shrink-0 text-[12px] text-ink-400 transition hover:text-ink-700"
          >
            📅 약속
          </button>
        )}
      </div>

      {/* 상대 지갑주소 — 복사·QR. 송금 실수는 되돌릴 수 없으니 재확인을 유도한다. */}
      {walletOpen && room.partnerWalletAddress && (
        <div className="mt-2 rounded-xl bg-surface p-3 shadow-card">
          <p className="mb-2 text-[13px] font-medium text-ink-900">
            {room.partnerNickname}님의 지갑주소
          </p>
          <WalletPanel address={room.partnerWalletAddress} />
          <p className="mt-2 text-[12px] text-ink-400">
            송금 전에 대화로 주소가 맞는지 한 번 더 확인하세요. 잘못 보낸 코인은 되돌릴 수 없어요.
          </p>
        </div>
      )}

      {reported && <Notice tone="info">신고가 접수됐어요. 운영자가 확인할게요.</Notice>}

      {/* 약속 배너 — **활성 약속이 있을 때만** 상단에 슬림하게. 대화가 주(主)라, 없을 땐 표시하지 않는다. */}
      {!room.closed && room.appointment && room.appointment.status !== 'cancelled' && (
        <div className="mt-2 rounded-xl bg-surface p-3 shadow-card">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[14px]">📅</span>
                <span className="text-[14px] font-medium text-ink-900">
                  {formatApptWhen(room.appointment.scheduledAt)} · {room.appointment.placeName}
                </span>
                <span
                  className={`ml-auto rounded-md px-2 py-0.5 text-[11px] font-medium ${
                    room.appointment.status === 'confirmed'
                      ? 'bg-tomato-50 text-tomato-600'
                      : 'bg-ink-100 text-ink-500'
                  }`}
                >
                  {room.appointment.status === 'confirmed' ? '확정' : '제안됨'}
                </span>
              </div>

              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                {room.appointment.status === 'proposed' && room.appointment.canConfirm && (
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void apptAction(`/api/appointments/${room.appointment!.id}/confirm`)
                    }
                  >
                    수락하기
                  </Button>
                )}
                {room.appointment.status === 'proposed' && room.appointment.proposedByMe && (
                  <span className="text-[12px] text-ink-400">상대의 수락을 기다리는 중…</span>
                )}

                {room.appointment.status === 'confirmed' && (
                  <>
                    <a
                      href={`/api/appointments/${room.appointment.id}/ics`}
                      download
                      className="rounded-lg bg-ink-100 px-3 py-1.5 text-[13px] font-medium text-ink-700 transition hover:bg-ink-200"
                    >
                      📆 캘린더에 저장
                    </a>
                    {room.appointment.canArrive &&
                      (room.appointment.arrived ? (
                        <span className="text-[12px] text-ink-400">도착 알림을 보냈어요</span>
                      ) : (
                        <Button size="sm" disabled={busy} onClick={() => setArriveOpen((v) => !v)}>
                          📍 도착했어요
                        </Button>
                      ))}
                  </>
                )}

                <button
                  disabled={busy}
                  onClick={() => void apptAction(`/api/appointments/${room.appointment!.id}/cancel`)}
                  className="ml-auto text-[12px] text-ink-400 transition hover:text-ink-700"
                >
                  약속 취소
                </button>
              </div>

              {arriveOpen && room.appointment.status === 'confirmed' && (
                <div className="mt-2.5 flex items-end gap-2">
                  <TextField
                    value={arriveHint}
                    placeholder="어디 계세요? (선택: 소파 쪽 창가)"
                    className="flex-1 text-[13px]"
                    onChange={(e) => setArriveHint(e.target.value)}
                  />
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      void apptAction(`/api/appointments/${room.appointment!.id}/arrive`, {
                        locationHint: arriveHint.trim(),
                      }).then(() => {
                        setArriveOpen(false);
                        setArriveHint('');
                      })
                    }
                  >
                    알리기
                  </Button>
                </div>
              )}

              {/* D7: 확정 후 텔레그램 미연동자에게 리마인더 연동 유도. */}
              {room.appointment.status === 'confirmed' && !telegramLinked && (
                <p className="mt-2.5 rounded-lg bg-ink-50 p-2.5 text-[12px] leading-relaxed text-ink-500">
                  🔔 텔레그램을 연동하면 약속 <b>30분 전 리마인더</b>를 받을 수 있어요.{' '}
                  <Link to="/settings" className="font-medium text-tomato-600">
                    연동하기
                  </Link>
                </p>
              )}
            </div>
        </div>
      )}

      {/* 메시지 — 창 내부에서만 스크롤(높이 제한). 입력창이 항상 화면에 보이게 한다. */}
      <div className="mt-3 max-h-[48vh] min-h-[220px] space-y-2 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex h-[220px] flex-col items-center justify-center px-6 text-center">
            <p className="text-[14px] text-ink-500">판매자와 대화를 시작해보세요.</p>
            <p className="mt-1 text-[12px] text-ink-400">
              아래 입력창에 궁금한 점을 자유롭게 물어보세요.
            </p>
          </div>
        ) : (
          messages.map((m) => <Bubble key={m.id} m={m} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* 약속 제안 폼 — 📅 버튼으로 여는 부가 기능. 활성 약속이 없을 때만. */}
      {!room.closed &&
        proposeOpen &&
        !(room.appointment && room.appointment.status !== 'cancelled') && (
          <div className="mb-2 rounded-xl bg-surface p-3 shadow-card">
            <p className="mb-2 text-[13px] font-medium text-ink-700">약속 잡기 (선택)</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <TextField
                type="datetime-local"
                value={proposeAt}
                min={toLocalInput(new Date())}
                className="text-[13px]"
                onChange={(e) => setProposeAt(e.target.value)}
              />
              <Select
                value={proposePlace}
                className="text-[13px]"
                onChange={(e) => setProposePlace(e.target.value)}
              >
                {places.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="mt-2 flex justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => setProposeOpen(false)}>
                취소
              </Button>
              <Button
                size="sm"
                disabled={busy || !proposeAt || !proposePlace}
                onClick={() => void proposeAppointment()}
              >
                제안하기
              </Button>
            </div>
          </div>
        )}

      {/* 입력 영역 — 메시지 높이를 제한했으므로 스크롤 없이 바로 보인다. */}
      {room.closed ? (
        <div className="py-4 text-center text-[13px] text-ink-400">종료된 대화예요.</div>
      ) : (
        <div className="mt-2">
          {/* 시작 문구 제안 — 빈 방에서만. 누르면 입력창에 채워져 그대로 보내거나 고쳐 보낸다. */}
          {messages.length === 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setInput(s)}
                  className="rounded-full bg-surface px-3 py-1.5 text-[12px] text-ink-600 shadow-card transition hover:bg-ink-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {/* 매너 안내 — 채팅창이 열리면 늘 보인다. */}
          <p className="mb-2 text-center text-[11px] text-ink-400">
            🤝 서로 존중하는 매너 있는 대화를 부탁드려요.
          </p>
          <div className="flex items-end gap-2">
            {/* 신고 — 입력창 왼쪽 상시 버튼. 누르면 신고 모달이 뜬다. */}
            <button
              type="button"
              title="신고하기"
              onClick={() => setReportOpen(true)}
              className="grid size-10 shrink-0 place-items-center rounded-full bg-surface text-[16px] shadow-card transition hover:bg-ink-50"
            >
              🚩
            </button>
            <TextArea
              rows={1}
              className="flex-1 resize-none"
              value={input}
              placeholder="메시지 입력"
              maxLength={CHAT_LIMITS.bodyMax}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter 전송, Shift+Enter 줄바꿈.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <Button disabled={busy || !input.trim()} onClick={() => void send()}>
              보내기
            </Button>
          </div>
        </div>
      )}

      <Notice>{error}</Notice>

      {/* 신고 모달 — 입력창 왼쪽 🚩 버튼으로 열린다. */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4">
          <div className="w-full max-w-md rounded-t-2xl bg-surface p-5 shadow-xl sm:rounded-2xl">
            <p className="text-[16px] font-semibold text-ink-900">대화 신고</p>
            <p className="mt-1 text-[12px] leading-relaxed text-ink-400">
              선택한 내용이 운영자에게 전달돼요. 활동명 외 개인정보는 담기지 않습니다.
            </p>

            <p className="mt-4 mb-1.5 text-[13px] font-medium text-ink-700">신고 유형</p>
            <div className="flex flex-wrap gap-1.5">
              {REPORT_CATEGORIES.map((cat) => (
                <button
                  key={cat.code}
                  type="button"
                  onClick={() => setReportCategory(cat.code)}
                  className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
                    reportCategory === cat.code
                      ? cat.code === 'harassment'
                        ? 'bg-tomato-600 text-white'
                        : 'bg-ink-900 text-ink-50'
                      : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            {reportCategory === 'harassment' && (
              <p className="mt-2 rounded-lg bg-tomato-50 p-2.5 text-[12px] leading-relaxed text-tomato-700">
                직장 내 괴롭힘은 <b>관리자가 사내 공식 채널에 대리 신고</b>해 드릴 수 있어요. 아래에
                상황을 남겨 말씀해 주시면 관리자가 직접 도와드리겠습니다.
              </p>
            )}

            <TextArea
              rows={3}
              className="mt-3"
              value={reportReason}
              placeholder="어떤 점이 문제였는지 적어주세요 (선택)"
              onChange={(e) => setReportReason(e.target.value)}
            />

            <div className="mt-3 space-y-2">
              <Checkbox checked={reportAttach} onChange={setReportAttach}>
                대화 캡처 함께 첨부 (일시 포함)
              </Checkbox>
              <Checkbox checked={reportBlock} onChange={setReportBlock}>
                차단하고 대화방 나가기
              </Checkbox>
            </div>

            <div className="mt-5 flex justify-end gap-1.5">
              <Button variant="ghost" onClick={() => setReportOpen(false)}>
                취소
              </Button>
              <Button variant="danger" disabled={busy} onClick={() => void submitReport()}>
                관리자에게 신고
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
